/** Shared Money lab state — ported from public/app/money.js */

export const TOOL = "money";
export const DRAFT_KEY = (id) => `yc-money-draft:${id}`;

export const LIFE_CATS = [
  { id: "phone", en: "Phone / data", fr: "Téléphone / données" },
  { id: "food", en: "Food / snacks", fr: "Bouffe / collations" },
  { id: "gym", en: "Gym / sports", fr: "Gym / sports" },
  { id: "transit", en: "Transit", fr: "Transport" },
  { id: "movies", en: "Movies / outings", fr: "Ciné / sorties" },
  { id: "dating", en: "Dating / hangouts", fr: "Dates / sorties amis" },
  { id: "clothes", en: "Clothes", fr: "Vêtements" },
  { id: "gaming", en: "Games / apps", fr: "Jeux / apps" },
  { id: "other", en: "Other", fr: "Autre" },
];

export const DRIVE_CATS = [
  { id: "gas", en: "Gas", fr: "Essence" },
  { id: "insurance", en: "Car insurance share", fr: "Part assurance auto" },
  { id: "car", en: "Car / rides", fr: "Auto / trajets" },
];

export const PERSONAS = [
  {
    id: "school",
    ages: "13–15",
    en: "School life",
    fr: "Vie scolaire",
    enHint: "Allowance, snacks, outings, and saving for something you want.",
    frHint: "Allocation, collations, sorties et épargne pour un objectif.",
    sample: { income: 120, phone: 0, food: 25, transit: 10, movies: 20, clothes: 15, gaming: 10 },
  },
  {
    id: "first_job",
    ages: "16–18",
    en: "First job",
    fr: "Premier emploi",
    enHint: "Part-time pay, phone, transit, graduation, and your first bigger goals.",
    frHint: "Salaire à temps partiel, téléphone, transport et premiers grands objectifs.",
    sample: { income: 620, phone: 55, food: 90, transit: 75, movies: 45, clothes: 35, gaming: 20 },
  },
  {
    id: "next_step",
    ages: "19–21",
    en: "Next step",
    fr: "Prochaine étape",
    enHint: "Rent share, tuition, groceries, work, and planning more independently.",
    frHint: "Part de loyer, études, épicerie, travail et plus d’autonomie.",
    sample: { income: 1850, phone: 65, food: 350, transit: 140, movies: 55, clothes: 45, gaming: 20, other: 760 },
  },
];

export const GOAL_PRESETS = [
  { id: "buffer", en: "Build a safety cushion", fr: "Créer un coussin de sécurité", amount: 250 },
  { id: "school", en: "Pay for school or training", fr: "Payer études ou formation", amount: 500 },
  { id: "purchase", en: "Save for something important", fr: "Épargner pour quelque chose d’important", amount: 300 },
  { id: "independence", en: "Prepare for more independence", fr: "Préparer plus d’autonomie", amount: 750 },
];

export const OFFER_PRESETS = [
  { en: "Tutoring", fr: "Tutorat", materials: 2, minutes: 60, price: 20 },
  { en: "Lawn or snow help", fr: "Pelouse ou neige", materials: 4, minutes: 45, price: 25 },
  { en: "Custom art or design", fr: "Art ou design personnalisé", materials: 8, minutes: 90, price: 35 },
  { en: "Event support", fr: "Aide événementielle", materials: 3, minutes: 60, price: 22 },
];

export const TERMS = [
  {
    id: "need_want",
    en: { term: "Need vs want", def: "Need = must-have to get through the month. Want = nice-to-have. Both are real — no shame." },
    fr: { term: "Besoin vs envie", def: "Besoin = indispensable pour passer le mois. Envie = agréable. Les deux existent — pas de honte." },
  },
  {
    id: "leftover",
    en: { term: "Leftover", def: "Income minus spends. The number that tells you if the month still breathes." },
    fr: { term: "Reste", def: "Revenus moins dépenses. Le chiffre qui dit si le mois respire encore." },
  },
  {
    id: "pay_first",
    en: { term: "Pay yourself first", def: "Move a small save amount before fun money — even $10 counts." },
    fr: { term: "Se payer d’abord", def: "Déplace un petit montant épargne avant le fun — même 10 $ compte." },
  },
  {
    id: "crash",
    en: { term: "Crash week", def: "One overspend. Name it, cut one line next week only, tell a parent one sentence. Restart is allowed." },
    fr: { term: "Semaine crash", def: "Un dépassement. Nomme-le, coupe une ligne la semaine suivante seulement, dis une phrase à un parent. Recommencer est OK." },
  },
];

/** Scenario choices include budget deltas for consequence sim */
export const SCENARIOS = [
  {
    id: "phone_break",
    en: "Your phone screen cracks ($150).",
    fr: "Écran de téléphone cassé (150 $).",
    choices: [
      { id: "save", en: "Use savings / wait", fr: "Épargne / attendre", xp: 15, delta: { other: 150 } },
      { id: "cut", en: "Cut outings this month", fr: "Couper les sorties ce mois", xp: 12, delta: { movies: -40, dating: -20, other: 150 } },
      { id: "ask", en: "Ask parent for a loan plan", fr: "Plan de prêt avec parent", xp: 10, delta: { other: 75 } },
    ],
  },
  {
    id: "friend_trip",
    en: "Friends plan a day trip ($60).",
    fr: "Les amis planifient une sortie (60 $).",
    choices: [
      { id: "go", en: "Go — cut gaming/apps", fr: "Y aller — couper jeux/apps", xp: 12, delta: { movies: 60, gaming: -20 } },
      { id: "skip", en: "Skip and keep leftover", fr: "Passer et garder le reste", xp: 14, delta: {} },
      { id: "split", en: "Go half — pack snacks", fr: "Moitié — apporter collations", xp: 15, delta: { movies: 30, food: 10 } },
    ],
  },
  {
    id: "missed_shift",
    en: "You miss a shift (−$80 income).",
    fr: "Tu manques un quart (−80 $ de revenu).",
    choices: [
      { id: "cut", en: "Cut wants this week", fr: "Couper les envies cette semaine", xp: 14, incomeDelta: -80, delta: { movies: -25, gaming: -15, dating: -20 } },
      { id: "hold", en: "Hold goal save for now", fr: "Mettre l’épargne objectif en pause", xp: 12, incomeDelta: -80, delta: {} },
      { id: "extra", en: "Ask for an extra shift later", fr: "Demander un quart de plus plus tard", xp: 13, incomeDelta: -40, delta: {} },
    ],
  },
  {
    id: "gift",
    en: "You want a birthday gift for a friend ($40).",
    fr: "Tu veux un cadeau d’anniversaire (40 $).",
    choices: [
      { id: "diy", en: "DIY / low-cost gift", fr: "Cadeau DIY / petit budget", xp: 14, delta: { other: 15 } },
      { id: "buy", en: "Buy — cut one want", fr: "Acheter — couper une envie", xp: 12, delta: { other: 40, movies: -20 } },
      { id: "wait", en: "Wait until next pay", fr: "Attendre la prochaine paie", xp: 11, delta: {} },
    ],
  },
];

export const BUSINESS_SCENARIOS = [
  { id: "slow", en: "Sales are half of plan", fr: "Ventes à la moitié du plan", factor: 0.5 },
  { id: "materials_up", en: "Materials cost jumps 30%", fr: "Matériaux +30 %", costFactor: 1.3 },
  { id: "refund", en: "One refund", fr: "Un remboursement", refund: 1 },
];

export const BADGES = {
  terms_clear: { en: "Need Navigator", fr: "Navigateur besoins" },
  first_budget: { en: "Budget Builder", fr: "Bâtisseur de budget" },
  scenario: { en: "Trade-off Thinker", fr: "Pro des compromis" },
  saver: { en: "Goal Guardian", fr: "Gardien d’objectif" },
  life_clear: { en: "Run Your Month", fr: "Maîtrise ton mois" },
  biz_start: { en: "Pricing Pro", fr: "Pro du prix" },
  crash_clear: { en: "Crash-Week Comeback", fr: "Retour semaine crash" },
  breakeven: { en: "Break-even Boss", fr: "Boss du seuil" },
  money_mentor: { en: "Money Mentor", fr: "Mentor argent" },
};

export const FOCUS = {
  money_l1_prepare: ["onboarding", "terms", "income", "meter"],
  money_l1_practice: ["meter", "map", "drive", "feedback", "chart"],
  money_l1_produce: ["meter", "map", "goals", "scenarios", "save", "feedback", "chart"],
  money_l1_prove: ["meter", "scenarios", "crash", "weeklog", "ontario", "feedback", "chart"],
  money_l1_present: ["meter", "share"],
  money_l2_prepare: ["business", "meter"],
  money_l2_practice: ["business", "pricing"],
  money_l2_produce: ["business", "pricing"],
  money_l2_prove: ["business", "pricing", "bizscenarios", "ontario"],
  money_l2_present: ["business", "share", "forecast"],
};

export function isFr() {
  if (typeof document !== "undefined" && document.documentElement.lang.startsWith("fr")) return true;
  try {
    return localStorage.getItem("yc-lang") === "fr";
  } catch (_) {
    return false;
  }
}

export function t(en, frText) {
  return isFr() ? frText : en;
}

export function defaultState() {
  return {
    profile: { persona: "", goal: "", goalLabel: "", goalAmount: 0, mode: "sample", session: 15, complete: false },
    drives: false,
    driveSet: false,
    income: [{ id: "allowance", label: t("Allowance / job", "Allocation / job"), amount: 0 }],
    categories: LIFE_CATS.map((c) => ({ id: c.id, amount: 0, kind: "" })),
    goals: [],
    priorities: ["", "", ""],
    habit: "",
    termsFlipped: [],
    termsQuizOk: false,
    labOpened: false,
    xp: 0,
    badges: [],
    scenariosDone: [],
    scenarioAnswers: {},
    scenarioReflections: {},
    scenarioImpacts: {},
    weekLog: "",
    flexibleCut: "",
    parentPayOk: false,
    budgetSaved: false,
    planCopied: false,
    breakEvenOk: false,
    bizShowDone: false,
    business: {
      offer: "",
      fixedCost: 10,
      materialCost: 0,
      minutes: 60,
      price: 0,
      sales: 5,
      priceTests: [],
      runway: "",
      books: "",
      refuse: "",
      scenariosDone: [],
      nextChange: "",
    },
    budgetHealth: "empty",
  };
}

export function normalizeState(raw) {
  const base = defaultState();
  const next = { ...base, ...(raw || {}) };
  next.profile = { ...base.profile, ...((raw || {}).profile || {}) };
  next.business = { ...base.business, ...((raw || {}).business || {}) };
  next.categories = Array.isArray(next.categories) ? next.categories : base.categories;
  next.income = Array.isArray(next.income) ? next.income : base.income;
  next.scenarioAnswers = next.scenarioAnswers || {};
  next.scenarioReflections = next.scenarioReflections || {};
  next.scenarioImpacts = next.scenarioImpacts || {};
  next.business.scenariosDone = next.business.scenariosDone || [];
  next.business.priceTests = next.business.priceTests || [];
  next.priorities = Array.isArray(next.priorities) ? next.priorities : ["", "", ""];
  return next;
}

export function sum(list, key) {
  return (list || []).reduce((a, x) => a + (Number(x[key]) || 0), 0);
}

export function health(income, spend) {
  if (income <= 0 && spend <= 0) return "empty";
  const left = income - spend;
  if (left < 0) return "over";
  if (left < income * 0.1) return "tight";
  return "healthy";
}

export function award(state, badgeId, xp) {
  if (badgeId && (state.badges || []).includes(badgeId)) {
    if (xp) state.xp = (state.xp || 0) + xp;
    return false;
  }
  state.xp = (state.xp || 0) + (xp || 0);
  if (badgeId && !(state.badges || []).includes(badgeId)) {
    state.badges = [...(state.badges || []), badgeId];
  }
  return true;
}

export function catsFor(state) {
  return state.drives ? [...LIFE_CATS, ...DRIVE_CATS] : LIFE_CATS;
}

export function filledCats(state) {
  const ids = new Set(catsFor(state).map((c) => c.id));
  return (state.categories || []).filter((c) => ids.has(c.id) && Number(c.amount) > 0);
}

export function applyScenarioImpact(state, scenarioId, choice) {
  const cats = (state.categories || []).map((c) => ({ ...c }));
  const income = (state.income || []).map((i) => ({ ...i }));
  const delta = choice?.delta || {};
  Object.entries(delta).forEach(([id, amount]) => {
    let row = cats.find((c) => c.id === id);
    if (!row) {
      row = { id, amount: 0, kind: "" };
      cats.push(row);
    }
    row.amount = Math.max(0, Number(row.amount) + Number(amount));
  });
  if (choice?.incomeDelta) {
    if (!income.length) income.push({ id: "impact", label: t("Income", "Revenus"), amount: 0 });
    income[0].amount = Math.max(0, Number(income[0].amount) + Number(choice.incomeDelta));
  }
  return {
    ...state,
    categories: cats,
    income,
    scenarioImpacts: {
      ...(state.scenarioImpacts || {}),
      [scenarioId]: { choiceId: choice.id, delta, incomeDelta: choice.incomeDelta || 0 },
    },
  };
}

export function totals(state) {
  const incomeTotal = sum(state.income, "amount");
  const cats = catsFor(state);
  const spendTotal = sum(
    state.categories.filter((c) => cats.some((x) => x.id === c.id)),
    "amount"
  );
  const left = incomeTotal - spendTotal;
  return { incomeTotal, spendTotal, left, budgetHealth: health(incomeTotal, spendTotal) };
}

export function missionsFromState(state) {
  const { incomeTotal } = totals(state);
  const mapOk = filledCats(state).length >= 3;
  const nwOk = (state.categories || []).filter((c) => c.kind === "need" || c.kind === "want").length >= 3;
  const missions = [];
  if (state.termsQuizOk || (state.termsFlipped || []).length >= TERMS.length) {
    missions.push("money_l1_prepare_terms");
  }
  if (state.labOpened && state.profile?.complete) missions.push("money_l1_prepare_lab");
  if (incomeTotal > 0) missions.push("money_l1_prepare_income");
  if (mapOk) missions.push("money_l1_practice_map");
  if (state.driveSet) missions.push("money_l1_practice_drive");
  if (nwOk) missions.push("money_l1_practice_nw");
  if (state.budgetSaved && incomeTotal > 0) missions.push("money_l1_produce_month");
  if ((state.priorities || []).filter(Boolean).length >= 3) missions.push("money_l1_produce_priorities");
  if ((state.scenariosDone || []).length >= 1) missions.push("money_l1_produce_scenario");
  if ((state.scenariosDone || []).length >= 2) missions.push("money_l1_prove_pay");
  if (
    state.flexibleCut ||
    Object.values(state.scenarioReflections || {}).filter((value) => String(value).trim()).length >= 2
  ) {
    missions.push("money_l1_prove_cut");
  }
  if (state.planCopied) missions.push("money_l1_present_show");
  if ((state.habit || "").trim()) missions.push("money_l1_present_reflect");
  if ((state.business?.offer || "").trim()) {
    missions.push("money_l2_prepare_terms", "money_l2_prepare_offer", "money_l2_prepare_tools");
  }
  if ((state.business?.priceTests || []).length >= 3) missions.push("money_l2_practice_price");
  if (
    Number(state.business?.materialCost) >= 0 &&
    Number(state.business?.minutes) > 0 &&
    Number(state.business?.price) > 0
  ) {
    missions.push("money_l2_practice_costs");
  }
  if ((state.business?.books || "").trim()) missions.push("money_l2_produce_cash", "money_l2_produce_books");
  if (state.breakEvenOk) missions.push("money_l2_prove_breakeven");
  if ((state.business?.scenariosDone || []).length >= 1) missions.push("money_l2_prove_fix");
  if (state.bizShowDone) missions.push("money_l2_present_show");
  if ((state.business?.nextChange || "").trim()) missions.push("money_l2_present_learn");
  if (
    missions.includes("money_l1_produce_month") &&
    missions.includes("money_l1_produce_scenario") &&
    missions.includes("money_l1_present_show")
  ) {
    award(state, "life_clear", 0);
  }
  return [...new Set(missions)];
}

export function focusSet(weekKey) {
  return new Set(FOCUS[weekKey] || ["meter", "map", "scenarios", "save"]);
}

export function show(focus, key) {
  return focus.has(key);
}
